import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import Page from '@components/Page';
import { GET_APP_NAVIGATION_QUERY } from '@cvent/planner-navigation/client';
import {
  addApolloCacheProps,
  ApolloCacheProps,
  initApolloClient,
  SchemaLinkApolloClientFactory
} from '@cvent/apollo-client';
import { InMemoryCache } from '@apollo/client';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { context, schema } from '@pages/api/graphql';
import Button from '@cvent/carina/components/Button';
import { CventVideoEditor } from '@cvent/video-editor-js';
import { NoSsr } from '@components/NoSsr';
import * as config from 'src/AppConfig';
import { createProjectWithOptions } from 'src/common/project';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { AUTH_COOKIE_NAME, authClient, cookieManager } from 'src/common/authClient';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import getConfig from 'next/config';
import { isWhitelistedRegion } from 'src/utils';
import { TextTrackSourceInput } from '@pages/api/projects';
import { MediaType, TextTrackKind } from '@schema/types';
import { NavMetadata } from '@cvent/planner-navigation/types';

const LOG = LoggerFactory.create('src/pages/index');

const navMetadata: NavMetadata = {
  url: '/',
  staticRouteId: '/',
  topParentId: ''
};

const { publicRuntimeConfig } = getConfig();

type IndexPageProps = ApolloCacheProps & {
  projectId: string;
  videoEditorUrl: string;
  environment: string;
  locale?: string;
};

export async function getServerSideProps(
  serverSideContext: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<IndexPageProps>> {
  const client = initApolloClient({}, new SchemaLinkApolloClientFactory({}, schema, context), {
    cache: new InMemoryCache()
  });

  await client.query({
    query: GET_APP_NAVIGATION_QUERY,
    variables: { navMetadata }
  });

  const props = addApolloCacheProps(client, {});

  let videoId = '7729b831-2211-40f9-9b58-6ea5a60aefd2';
  if (serverSideContext.query.videoId) {
    videoId = serverSideContext.query.videoId as string;
  }

  let environment = 'S606';
  if (serverSideContext.query.environment) {
    environment = serverSideContext.query.environment as string;
  }

  const chosenProjectId = serverSideContext.query.projectId || null;

  if (chosenProjectId) {
    return {
      props: {
        ...props,
        projectId: chosenProjectId as string,
        videoEditorUrl: config.getVideoEditorEndpoint(),
        environment
      }
    };
  }

  let accountMappingId = 'd0808ab4-55fd-45e6-a888-283087dae98a';
  if (serverSideContext.query.accountMappingId) {
    accountMappingId = serverSideContext.query.accountMappingId as string;
  }

  const textTrackKind = (serverSideContext.query.textTrackKind as TextTrackKind) || TextTrackKind.Subtitles;
  const textTrackLocale = (serverSideContext.query.textTrackLocale as string) || 'en-US';
  const textTrackId = (serverSideContext.query.textTrackId as string) || null;
  const userIds = ['test'];

  const cookies = cookieManager(serverSideContext);
  if (!cookies.getCookie(AUTH_COOKIE_NAME)) {
    try {
      const userToken = await authClient.createAccessToken({
        authorization: {
          metadata: {
            UserStub: 'test'
          },
          roles: ['projects:read', 'projects:write']
        }
      });
      cookies.setCookie(AUTH_COOKIE_NAME, userToken?.accessToken);
    } catch (e) {
      LOG.error('Error creating cookie', e);
    }
  }

  const textTrackSource: TextTrackSourceInput = {
    videoId,
    textTrackId,
    locale: textTrackLocale,
    kind: textTrackKind,
    type: MediaType.TextTrack,
    primary: true
  };

  const optionsRequest = {
    accountMappingId,
    userIds,
    options: {
      sources: [textTrackSource]
    }
  };

  const projectId = await createProjectWithOptions(optionsRequest, environment);

  if (!isWhitelistedRegion) {
    return {
      notFound: true
    };
  }

  // optional locale override through query param to test translations
  let locale: string = null;
  if (serverSideContext.query?.locale) {
    locale = serverSideContext.query.locale as string;
  }

  return {
    props: {
      ...props,
      projectId,
      videoEditorUrl: config.getVideoEditorEndpoint(),
      environment,
      locale
    }
  };
}

export default function IndexPage({ videoEditorUrl, projectId, environment, locale }: IndexPageProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'VIDEO' | 'SUBTITLES' | 'CHAPTERS'>('VIDEO');

  LOG.debug({ videoEditorUrl, projectId });

  const toggleEditor = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  useEffect(() => {
    // Temp logging of env/region/version for debugging
    // eslint-disable-next-line no-console
    console.log(
      `AppVersion - ${publicRuntimeConfig.CVENT_REGION}::${publicRuntimeConfig.DD_ENV}::${publicRuntimeConfig.DD_VERSION}`
    );
  }, []);

  const handleOpenEditor = useCallback(
    mode => {
      setEditorMode(mode);
      toggleEditor();
    },
    [toggleEditor]
  );

  const handleRenderComplete = useCallback((job): void => {
    // eslint-disable-next-line no-console
    console.log(`parent got event: ${JSON.stringify(job)}`);
  }, []);

  return (
    <Page navMetadata={navMetadata}>
      <div>
        <div
          css={css`
            display: flex;
            gap: 15px;
          `}
        >
          <Button
            text="Launch Subtitles Editor"
            appearance="filled"
            size="l"
            variant="tertiary"
            onClick={(): void => handleOpenEditor('SUBTITLES')}
          />
          <Button
            text="Launch Chapters Editor"
            appearance="filled"
            size="l"
            variant="tertiary"
            onClick={(): void => handleOpenEditor('CHAPTERS')}
          />
          <Button
            text="Launch Video Editor"
            appearance="filled"
            size="l"
            variant="tertiary"
            onClick={(): void => handleOpenEditor('VIDEO')}
          />
          <input {...injectTestId('project-id')} type="hidden" value={projectId} />
        </div>
      </div>
      <NoSsr>
        <CventVideoEditor
          mode={editorMode}
          url={videoEditorUrl}
          environment={environment}
          isOpen={open}
          onClose={toggleEditor}
          onRenderComplete={handleRenderComplete}
          locale={locale}
          onError={(err): void => {
            // eslint-disable-next-line no-console
            console.error('Received error from video-editor-js', err);
            if (open) {
              toggleEditor();
            }
          }}
          projectId={projectId}
          theme="DARK"
          display="MODAL"
        />
      </NoSsr>
    </Page>
  );
}
